import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type AdminUser,
  useAdminUsersApi,
  useToggleSystemAdminApi,
} from "hooks/useAdminApi";
import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "utils/relative-time";

export const UsersPage: FC = () => {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useAdminUsersApi();
  const { toggleSystemAdmin } = useToggleSystemAdminApi();

  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const filteredUsers =
    data?.items?.filter(
      user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${user.familyName} ${user.givenName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    ) || [];

  const handleToggleSystemAdmin = async (user: AdminUser) => {
    if (
      !window.confirm(
        user.isSystemAdmin
          ? `${user.familyName} ${user.givenName} さんのシステム管理者権限を剥奪しますか？`
          : `${user.familyName} ${user.givenName} さんにシステム管理者権限を付与しますか？`,
      )
    ) {
      return;
    }

    setActionLoading(true);
    try {
      await toggleSystemAdmin(user.id, !user.isSystemAdmin);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>ユーザー管理</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>ユーザー管理</PageTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
        >
          ← ダッシュボードに戻る
        </Button>
      </div>

      {/* 検索 */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="名前またはメールアドレスで検索..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="input input-bordered input-sm w-full md:w-96"
        />
      </div>

      {/* テーブル */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>ユーザー</Table.HeaderCell>
              <Table.HeaderCell>メールアドレス</Table.HeaderCell>
              <Table.HeaderCell>システム管理者</Table.HeaderCell>
              <Table.HeaderCell>作成日</Table.HeaderCell>
              <Table.HeaderCell align="right">操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-500">
                  ユーザーが見つかりません
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <Table.Row key={user.id}>
                  <Table.Cell>
                    <div className="flex items-center gap-3">
                      <div className="avatar placeholder">
                        <div className="bg-neutral text-neutral-content rounded-full w-10">
                          <span>
                            {user.familyName?.charAt(0)}
                            {user.givenName?.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <Link to={`/admin/users/${user.id}`}>
                          {user.familyName} {user.givenName}
                        </Link>
                        {user.familyNameKana && user.givenNameKana && (
                          <div className="text-gray-500">
                            {user.familyNameKana} {user.givenNameKana}
                          </div>
                        )}
                      </div>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-gray-600">{user.email}</span>
                  </Table.Cell>
                  <Table.Cell>
                    {user.isSystemAdmin ? (
                      <StatusBadge tone="tag-neutral">
                        システム管理者
                      </StatusBadge>
                    ) : (
                      <StatusBadge tone="soft-neutral">
                        一般ユーザー
                      </StatusBadge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-gray-500">
                      {formatDate(user.createdAt)}
                    </span>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Button
                      onClick={() => handleToggleSystemAdmin(user)}
                      disabled={actionLoading}
                      size="xs"
                      variant={user.isSystemAdmin ? "warning" : "secondary"}
                    >
                      {user.isSystemAdmin ? "権限を剥奪" : "管理者に昇格"}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* 統計 */}
      <div className="mt-4 flex justify-between text-sm text-gray-600">
        <span>
          全 {data?.total || 0} 件中 {filteredUsers.length} 件を表示
        </span>
        <span>
          システム管理者:{" "}
          {data?.items?.filter(u => u.isSystemAdmin).length || 0} 名
        </span>
      </div>

      {/* 注意事項 */}
      <div className="mt-6">
        <div className="alert alert-info">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-bold">システム管理者について</h3>
            <div className="text-sm">
              システム管理者は全てのテナントとユーザーを管理できます。
              権限の付与・剥奪は慎重に行ってください。
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
